// src/views/reports/ProfitabilityReportView.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db } from '../../firebase/config';
import { collection, query, onSnapshot, orderBy, where } from 'firebase/firestore';
import { startOfMonth, endOfMonth, format, parseISO } from 'date-fns';
import { ArrowLeft, TrendingUp, TrendingDown, Calendar, Download, ChevronDown, ChevronRight, ExternalLink, BarChart3 } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { exportToCSV } from '../../utils/csvExport'; // Import the new tool
import { canSeeLaborCost, rowsWithCostRates } from '../../utils/jobCosts'; // s6costing-4d8a2f63
import { useOrgJobCosts } from '../../hooks/useJobCosts'; // s6costing-4d8a2f63
import { netJobRevenue, jobCreditNotesForPeriod, toDateOrNull } from '../../utils/invoiceNetRevenue'; // netrev-6f2a8c53 (R1d-5) - revenue net of credit notes; cnperiod-5a3e7d92 - by the credit note's own date

const ProfitabilityReportView = ({ currentUserProfile }) => {
    // --- 1. HOOKS ---
    const [jobs, setJobs] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [creditedInvoices, setCreditedInvoices] = useState([]); // netrev-6f2a8c53 -- invoices carrying a credit note
    const [loading, setLoading] = useState(true);
    
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // When the drill-in's "Back" button returns here it passes ?expand=<jobId>
    // so we re-open and scroll to the row the user came from.
    const [expandedJobId, setExpandedJobId] = useState(() => searchParams.get('expand'));
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
    const expandedRowRef = useRef(null);

    const toggleExpand = (jobId) => setExpandedJobId(prev => prev === jobId ? null : jobId);

    // Scroll the returned-to row into view once its data has loaded.
    useEffect(() => {
        if (expandedJobId && !loading && expandedRowRef.current) {
            expandedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [expandedJobId, loading]);

    const requestSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
        }));
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-3" />;
        return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    };

    // --- 2. DATA FETCHING ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const qJobs = query(
            collection(db, `organizations/${orgId}/jobs`),
            orderBy('endAt', 'desc')
        );

        const unsubJobs = onSnapshot(qJobs, (snap) => {
            setJobs(snap.docs.map(doc => ({ ...doc.data(), id: doc.id }))); // s6costing-docid: doc id wins over a stale stored `id` field
        });

        const qExpenses = query(collection(db, `organizations/${orgId}/expenses`));
        const unsubExpenses = onSnapshot(qExpenses, (snap) => {
            setExpenses(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // netrev-6f2a8c53 -- R1d-5: the invoices that carry a credit note (none, for a company that
        // has never issued one), so a job's revenue can be shown net of them. Read-only.
        const unsubCredited = onSnapshot(
            query(collection(db, `organizations/${orgId}/invoices`), where('creditedTotal', '>', 0)),
            (snap) => setCreditedInvoices(snap.docs.map(d => ({ ...d.data(), id: d.id }))),
            () => setCreditedInvoices([]),
        );

        setLoading(false);

        return () => {
            unsubJobs();
            unsubExpenses();
            unsubCredited();
        };
    }, [currentUserProfile]);

    // s6costing-4d8a2f63 — S6b: labour cost rates come from jobCosts (Owner/Admin/Manager only),
    // falling back to a not-yet-migrated row's own rate. Display-only copies; nothing is saved.
    const { ratesByJob } = useOrgJobCosts(currentUserProfile?.organizationId, canSeeLaborCost(currentUserProfile));

    // --- 3. THE MATH ENGINE ---
    const reportData = useMemo(() => {
        const start = parseISO(startDate);
        const end = parseISO(endDate);
        end.setHours(23, 59, 59, 999);

        const filteredJobs = jobs.filter(job => {
            if (job.status !== 'Completed') return false;
            const jobDate = job.endAt?.toDate ? job.endAt.toDate() : new Date(job.endAt);
            return jobDate >= start && jobDate <= end;
        });

        // cnperiod-5a3e7d92 -- a credit note counts in the dates it was ISSUED. A listed job's row is
        // reduced only by its credit notes issued in these dates; credit notes issued in these dates
        // for a job that is NOT listed (completed earlier) come off the totals as their own line.
        // Both are empty when no invoice carries a credit note.
        const periodCredits = jobCreditNotesForPeriod(filteredJobs, jobs, creditedInvoices, start, end, (inv) => toDateOrNull(inv.createdAt) || toDateOrNull(inv.issuedAt) || toDateOrNull(inv.date));
        const processedJobs = filteredJobs.map(job => {
            const jobCredits = periodCredits.byJob[job.id] || [];
            const revenue = netJobRevenue(job, jobCredits.filter(c => c.inPeriod).reduce((s, c) => s + c.amount, 0)); // parseFloat(job.invoiceTotal) || 0 when no credit note was issued in these dates
            const costRows = rowsWithCostRates(job.jobCosting?.laborEntries, ratesByJob[job.id]); // s6costing-4d8a2f63
            const laborCost = costRows.reduce((sum, entry) => {
                const hours = parseFloat(entry.hours) || 0;
                const rate = parseFloat(entry.rate) || 0;
                return sum + (hours * rate);
            }, 0) || 0;

            const jobExpenses = expenses.filter(exp => exp.jobId === job.id);
            const materialCost = jobExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

            const totalCost = laborCost + materialCost;
            const netProfit = revenue - totalCost;
            const margin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

            return {
                ...job,
                revenue, laborCost, materialCost, totalCost, netProfit, margin,
                // Keep these for the expanded detail row
                laborEntries: costRows,
                jobExpenses,
                // revbridge-3c9e7a15 -- this job's credit notes, for the expanded row ([] for a job without one).
                creditNotes: jobCredits, // each { number, amount, date, inPeriod }
            };
        });

        // Sort
        processedJobs.sort((a, b) => {
            let aVal, bVal;
            if (sortConfig.key === 'date') {
                aVal = a.endAt?.toDate ? a.endAt.toDate() : new Date(0);
                bVal = b.endAt?.toDate ? b.endAt.toDate() : new Date(0);
            } else if (sortConfig.key === 'jobNumber') {
                aVal = parseInt(String(a.jobNumber || '0').replace(/\D/g, '')) || 0;
                bVal = parseInt(String(b.jobNumber || '0').replace(/\D/g, '')) || 0;
            } else {
                aVal = a[sortConfig.key] || 0;
                bVal = b[sortConfig.key] || 0;
            }
            if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });

        const totals = processedJobs.reduce((acc, job) => ({
            revenue: acc.revenue + job.revenue,
            costs: acc.costs + job.totalCost,
            profit: acc.profit + job.netProfit
        }), { revenue: 0, costs: 0, profit: 0 });

        // cnperiod-5a3e7d92 -- credit notes issued in these dates for jobs not listed above.
        const outsideCredits = periodCredits.outside;
        const outsideTotal = outsideCredits.reduce((s, c) => s + c.amount, 0);
        if (outsideTotal > 0) { totals.revenue -= outsideTotal; totals.profit -= outsideTotal; }

        const avgMargin = totals.revenue > 0 ? (totals.profit / totals.revenue) * 100 : 0;

        return { list: processedJobs, totals, avgMargin, outsideCredits, outsideTotal };

    }, [jobs, expenses, creditedInvoices, startDate, endDate, sortConfig, ratesByJob]);

    // --- 4. EXPORT HANDLER (New) ---
    const handleExport = () => {
        if (!reportData.list.length) {
            alert("No data to export");
            return;
        }

        // Prepare clean data for CSV (No messy objects)
        const csvData = reportData.list.map(job => ({
            Date: job.endAt?.toDate ? job.endAt.toDate().toLocaleDateString() : 'N/A',
            Client: job.clientName || 'Unknown',
            JobTitle: job.title || 'Untitled',
            Revenue: job.revenue.toFixed(2),
            Labor_Cost: job.laborCost.toFixed(2),
            Material_Cost: job.materialCost.toFixed(2),
            Total_Cost: job.totalCost.toFixed(2),
            Net_Profit: job.netProfit.toFixed(2),
            Margin_Percent: job.margin.toFixed(2) + '%'
        }));

        // Trigger the download
        exportToCSV(csvData, `Profitability_Report_${startDate}_to_${endDate}.csv`);
    };

    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---

    if (loading) return <div className="p-10 text-center text-gray-500">Calculating Profitability...</div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Job Profitability</h1>
                        <p className="text-gray-500 text-sm">Analyze margins on Completed jobs</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* DATE FILTERS */}
                    <div className="flex items-center gap-2 bg-white p-2 rounded-md border shadow-sm">
                        <Calendar size={18} className="text-gray-400" />
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-sm border-none focus:ring-0 text-gray-600"
                        />
                        <span className="text-gray-400">-</span>
                        <input 
                            type="date" 
                            value={endDate} 
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-sm border-none focus:ring-0 text-gray-600"
                        />
                    </div>
                    
                    {/* EXPORT BUTTON */}
                    <button 
                        onClick={handleExport}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors shadow-sm"
                    >
                        <Download size={18} />
                        Export
                    </button>
                </div>
            </div>

            {/* SUMMARY CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Total Revenue (Period)</p>
                    <h3 className="text-2xl font-bold text-gray-800">{formatCurrency(reportData.totals.revenue)}</h3>
                </div>
                <div className="bg-white p-6 rounded-lg border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Total Net Profit</p>
                    <h3 className={`text-2xl font-bold ${reportData.totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(reportData.totals.profit)}
                    </h3>
                </div>
                <div className="bg-white p-6 rounded-lg border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Average Margin</p>
                    <div className="flex items-center gap-2">
                        <h3 className={`text-2xl font-bold ${reportData.avgMargin >= 30 ? 'text-green-600' : reportData.avgMargin > 0 ? 'text-orange-500' : 'text-red-600'}`}>
                            {reportData.avgMargin.toFixed(1)}%
                        </h3>
                        {reportData.avgMargin >= 30 ? <TrendingUp size={20} className="text-green-600" /> : <TrendingDown size={20} className="text-red-600" />}
                    </div>
                </div>
            </div>

            {/* cnperiod-5a3e7d92 -- credit notes issued in these dates for jobs completed outside them. */}
            {reportData.outsideTotal > 0 && (
                <div className="mb-6 p-4 rounded-lg border border-orange-200 bg-orange-50 text-sm text-orange-800">
                    <p className="font-medium">The totals above include −{formatCurrency(reportData.outsideTotal)} of credit notes issued in these dates for jobs that are not in this list (completed outside these dates).</p>
                    <p className="mt-1">{reportData.outsideCredits.map((c, i) => `${i ? ' · ' : ''}${c.number || 'Credit note'}${c.jobNumber ? ` (job #${c.jobNumber})` : ''} −${formatCurrency(c.amount)}`).join('')}</p>
                </div>
            )}

            {/* DATA TABLE */}
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b">
                                {/* Chevron col */}
                                <th className="w-8 p-3"></th>
                                <th className="p-4 font-medium cursor-pointer whitespace-nowrap select-none" onClick={() => requestSort('jobNumber')}>Job #{getSortIndicator('jobNumber')}</th>
                                <th className="p-4 font-medium cursor-pointer whitespace-nowrap select-none" onClick={() => requestSort('date')}>Date{getSortIndicator('date')}</th>
                                <th className="p-4 font-medium cursor-pointer select-none" onClick={() => requestSort('title')}>Job / Client{getSortIndicator('title')}</th>
                                <th className="p-4 font-medium text-right cursor-pointer select-none" onClick={() => requestSort('revenue')}>Revenue{getSortIndicator('revenue')}</th>
                                <th className="p-4 font-medium text-right cursor-pointer select-none" onClick={() => requestSort('laborCost')}>Labor{getSortIndicator('laborCost')}</th>
                                <th className="p-4 font-medium text-right cursor-pointer select-none" onClick={() => requestSort('materialCost')}>Materials{getSortIndicator('materialCost')}</th>
                                <th className="p-4 font-medium text-right cursor-pointer select-none" onClick={() => requestSort('netProfit')}>Net Profit{getSortIndicator('netProfit')}</th>
                                <th className="p-4 font-medium text-right cursor-pointer select-none" onClick={() => requestSort('margin')}>Margin{getSortIndicator('margin')}</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-gray-700">
                            {reportData.list.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="p-8 text-center text-gray-500 italic">
                                        No completed jobs found in this date range.
                                    </td>
                                </tr>
                            ) : (
                                reportData.list.map(job => {
                                    const isExpanded = expandedJobId === job.id;
                                    return (
                                        <React.Fragment key={job.id}>

                                            {/* ── SUMMARY ROW ── */}
                                            <tr
                                                ref={isExpanded ? expandedRowRef : null}
                                                className={`border-b cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-gray-50'}`}
                                                onClick={() => toggleExpand(job.id)}
                                            >
                                                <td className="w-8 p-3 text-gray-400">
                                                    {isExpanded
                                                        ? <ChevronDown className="h-4 w-4 text-indigo-500" />
                                                        : <ChevronRight className="h-4 w-4" />}
                                                </td>
                                                <td className="p-4 font-mono font-semibold text-gray-600 whitespace-nowrap">
                                                    {job.jobNumber ? `#${String(job.jobNumber).replace(/^#/, '')}` : '—'}
                                                </td>
                                                <td className="p-4 whitespace-nowrap">
                                                    {job.endAt?.toDate ? job.endAt.toDate().toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className="p-4">
                                                    <div className="font-semibold text-gray-800">{job.title}</div>
                                                    <div className="text-xs text-gray-500">{job.clientName}</div>
                                                </td>
                                                <td className="p-4 text-right font-medium">{formatCurrency(job.revenue)}</td>
                                                <td className="p-4 text-right text-gray-600">{formatCurrency(job.laborCost)}</td>
                                                <td className="p-4 text-right text-gray-600">{formatCurrency(job.materialCost)}</td>
                                                <td className={`p-4 text-right font-bold ${job.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatCurrency(job.netProfit)}
                                                </td>
                                                <td className="p-4 text-right">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                                        job.margin >= 50 ? 'bg-green-100 text-green-800' :
                                                        job.margin >= 20 ? 'bg-blue-100 text-blue-800' :
                                                        job.margin > 0 ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {job.margin.toFixed(1)}%
                                                    </span>
                                                </td>
                                            </tr>

                                            {/* ── EXPANDED DETAIL ROW ── */}
                                            {isExpanded && (
                                                <tr className="bg-slate-50 border-b border-slate-200">
                                                    <td colSpan={9} className="px-6 py-5">
                                                        <div className="border-l-4 border-indigo-400 pl-4 space-y-5">

                                                            {/* LABOR BREAKDOWN */}
                                                            <div>
                                                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Labor Breakdown</h4>
                                                                {job.laborEntries.length > 0 ? (
                                                                    <table className="w-full text-sm">
                                                                        <thead>
                                                                            <tr className="text-xs text-gray-400 border-b border-slate-200">
                                                                                <th className="text-left pb-1 font-medium pr-8">Technician</th>
                                                                                <th className="text-right pb-1 font-medium pr-8">Clocked</th>
                                                                                <th className="text-right pb-1 font-medium pr-8">Allocated</th>
                                                                                <th className="text-right pb-1 font-medium pr-8">Rate</th>
                                                                                <th className="text-right pb-1 font-medium">Labor Cost</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {job.laborEntries.map((entry, i) => {
                                                                                const hrs = parseFloat(entry.hours) || 0;
                                                                                const rate = parseFloat(entry.rate) || 0;
                                                                                const allocated = entry.allocatedHours != null ? parseFloat(entry.allocatedHours) : null;
                                                                                const overBudget = allocated !== null && hrs > allocated;
                                                                                return (
                                                                                    <tr key={i} className="border-b border-slate-100 last:border-0">
                                                                                        <td className="py-1.5 pr-8 text-gray-700">{entry.name || '—'}</td>
                                                                                        <td className={`py-1.5 pr-8 text-right font-mono ${overBudget ? 'text-amber-600 font-semibold' : 'text-gray-700'}`}>
                                                                                            {hrs.toFixed(2)}h{overBudget && <span className="ml-1 text-xs">▲</span>}
                                                                                        </td>
                                                                                        <td className="py-1.5 pr-8 text-right font-mono text-gray-400">
                                                                                            {allocated !== null ? `${allocated.toFixed(2)}h` : '—'}
                                                                                        </td>
                                                                                        <td className="py-1.5 pr-8 text-right font-mono text-gray-500">
                                                                                            {rate > 0 ? `$${rate.toFixed(2)}/hr` : '—'}
                                                                                        </td>
                                                                                        <td className="py-1.5 text-right font-semibold text-gray-800">
                                                                                            {formatCurrency(hrs * rate)}
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <p className="text-sm text-gray-400 italic">No labor entries recorded.</p>
                                                                )}
                                                            </div>

                                                            {/* EXPENSES */}
                                                            <div>
                                                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Expenses &amp; Materials</h4>
                                                                {job.jobExpenses.length > 0 ? (
                                                                    <table className="w-full text-sm">
                                                                        <thead>
                                                                            <tr className="text-xs text-gray-400 border-b border-slate-200">
                                                                                <th className="text-left pb-1 font-medium pr-8">Description</th>
                                                                                <th className="text-left pb-1 font-medium pr-8">Category</th>
                                                                                <th className="text-right pb-1 font-medium">Amount</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {job.jobExpenses.map((exp, i) => (
                                                                                <tr key={i} className="border-b border-slate-100 last:border-0">
                                                                                    <td className="py-1.5 pr-8 text-gray-700">{exp.description || exp.name || '—'}</td>
                                                                                    <td className="py-1.5 pr-8 text-gray-500">{exp.category || '—'}</td>
                                                                                    <td className="py-1.5 text-right font-semibold text-gray-800">{formatCurrency(parseFloat(exp.amount) || 0)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <p className="text-sm text-gray-400 italic">No expenses recorded for this job.</p>
                                                                )}
                                                            </div>

                                                            {/* INVOICE + VIEW JOB */}
                                                            <div className="flex items-center justify-between pt-1">
                                                                <div className="text-sm text-gray-600">
                                                                    {job.invoiceTotal ? (
                                                                        <span>
                                                                            <span className="font-medium text-gray-700">Invoice Total:</span>
                                                                            {' '}<span className="font-semibold">{formatCurrency(parseFloat(job.invoiceTotal))}</span>
                                                                            {job.invoiceStatus && (
                                                                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                                    job.invoiceStatus === 'Paid' ? 'bg-green-100 text-green-700' :
                                                                                    job.invoiceStatus === 'Sent' ? 'bg-blue-100 text-blue-700' :
                                                                                    'bg-gray-100 text-gray-600'
                                                                                }`}>{job.invoiceStatus}</span>
                                                                            )}
                                                                            {/* revbridge-3c9e7a15 -- how the row's revenue is reached when the job has credit notes. */}
                                                                            {job.creditNotes.length > 0 && (
                                                                                <span className="block mt-1">
                                                                                    {job.creditNotes.map((c, ci) => (
                                                                                        <span key={ci} className={`block ${c.inPeriod ? 'text-orange-700' : 'text-gray-500'}`}>Credit note{c.number ? ` ${c.number}` : ''}{c.date ? ` (${c.date.toLocaleDateString()})` : ''}: −{formatCurrency(c.amount)}{c.inPeriod ? '' : ' — issued outside these dates, so not taken off here'}</span>
                                                                                    ))}
                                                                                    <span className="block"><span className="font-medium text-gray-700">Net revenue:</span> <span className="font-semibold">{formatCurrency(job.revenue)}</span></span>
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-gray-400 italic">No invoice linked.</span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-4">
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); navigate(`/reports/job/${job.id}`); }}
                                                                        className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                                                                    >
                                                                        Profit Detail <BarChart3 className="h-3.5 w-3.5" />
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${job.id}/edit`); }}
                                                                        className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                                                                    >
                                                                        View Full Job <ExternalLink className="h-3.5 w-3.5" />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </td>
                                                </tr>
                                            )}

                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProfitabilityReportView;